// =============================================
// extractCache — read each document ONCE
// =============================================
// Auto-reading a document (fetch the file, pull its text, OCR a scan, guess the
// fields) takes seconds. The result depends only on the file, which never changes, so:
//
//   1. memory  — this session: switching back to a document is instant;
//   2. server  — dms_document_extract (/api/dms/extract, migration 116): instant after a
//                reload and on any other device;
//   3. extract — only when neither has it; the result is then stored in both.
//
// One read per document at a time: a background pre-read and the user opening the same
// document share the same promise instead of both doing the work.
//
// Best-effort throughout — if the server can't be reached the document is simply read
// locally, exactly as before.
// =============================================
import type { ParsedReceiptData } from './receiptParser';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/** Bump when the reader changes what it extracts, so older stored results are re-read. */
export const EXTRACT_VERSION = 2;

/** Document text kept in the stored copy (the full text stays in memory for the session). */
const STORED_TEXT_CHARS = 8000;

const memory = new Map<number, ParsedReceiptData>();
const inflight = new Map<number, Promise<ParsedReceiptData | null>>();

function authToken(): string | null {
  try { return localStorage.getItem('akt_auth_token'); } catch { return null; }
}

export function hasCachedExtract(documentId: number): boolean {
  return memory.has(Number(documentId));
}

async function fetchStored(documentId: number): Promise<ParsedReceiptData | null> {
  const token = authToken();
  if (!token) return null;
  try {
    const res = await fetch(`${API_BASE}/dms/extract/${documentId}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return null;
    const json = await res.json();
    if (!json || !json.data || Number(json.version) !== EXTRACT_VERSION) return null;
    return json.data as ParsedReceiptData;
  } catch {
    return null;
  }
}

function store(documentId: number, parsed: ParsedReceiptData): void {
  const token = authToken();
  if (!token) return;
  const data = { ...parsed, rawText: String(parsed.rawText || '').slice(0, STORED_TEXT_CHARS) };
  // Fire and forget — a failed save only means the next device reads the file itself.
  fetch(`${API_BASE}/dms/extract/${documentId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ data, version: EXTRACT_VERSION, engine: 'local' }),
  }).catch(() => { /* best-effort */ });
}

/**
 * Add something learned later to a document's stored read (e.g. the handwritten date,
 * which needs a separate look) — in memory and on the server. Nothing happens if the
 * document has no stored read yet.
 */
export function amendDocExtract(documentId: number, patch: Partial<ParsedReceiptData>): void {
  const id = Number(documentId);
  const current = memory.get(id);
  if (!current) return;
  const next = { ...current, ...patch };
  memory.set(id, next);
  store(id, next);
}

/**
 * The auto-read result for a document: from memory, else the server, else by running
 * `extract` (and then remembering it in both). Null when the document can't be read.
 */
export function loadDocExtract(
  documentId: number,
  extract: () => Promise<ParsedReceiptData | null>,
): Promise<ParsedReceiptData | null> {
  const id = Number(documentId);
  const hit = memory.get(id);
  if (hit) return Promise.resolve(hit);
  const running = inflight.get(id);
  if (running) return running;

  const job = (async () => {
    const stored = await fetchStored(id);
    if (stored) { memory.set(id, stored); return stored; }
    const parsed = await extract();
    if (parsed) { memory.set(id, parsed); store(id, parsed); }
    return parsed;
  })().finally(() => { inflight.delete(id); });

  inflight.set(id, job);
  return job;
}
